// oxlint-disable react/no-array-index-key -- iterating over header indices

import { styled } from "goober";
import { useEffect, useMemo, useState } from "react";
import { Text } from "../Text";
import { FileRender } from "./index";
import type { FileRenderControl, FileSelection } from "./types";

const MAX_PREVIEW_BYTES = 256 * 1024;

type Props = {
  url: string;
  bleed?: number;
  selectedRanges?: readonly { from: number; to: number; color?: string }[];
  onSelect?: (selections: FileSelection[]) => void;
  onControls?: (controls: FileRenderControl[]) => void;
};

type ParsedHttp = {
  isResponse: boolean;
  method: string;
  path: string;
  statusCode: string;
  statusText: string;
  httpVersion: string;
  headers: Array<{ key: string; value: string }>;
  bodyBytes: Uint8Array | null;
};

/** Find `\r\n\r\n`, falling back to `\n\n` when carriage returns are absent. */
function findHeaderEnd(
  bytes: Uint8Array
): { offset: number; length: number } | null {
  for (let i = 0; i <= bytes.length - 4; i++) {
    if (
      bytes[i] === 0x0d &&
      bytes[i + 1] === 0x0a &&
      bytes[i + 2] === 0x0d &&
      bytes[i + 3] === 0x0a
    ) {
      return { offset: i, length: 4 };
    }
  }
  for (let i = 0; i <= bytes.length - 2; i++) {
    if (bytes[i] === 0x0a && bytes[i + 1] === 0x0a) {
      return { offset: i, length: 2 };
    }
  }
  return null;
}

function parseHttpMessage(data: ArrayBuffer): ParsedHttp {
  const bytes = new Uint8Array(data);
  const sep = findHeaderEnd(bytes);
  const headerEnd = sep ? sep.offset : bytes.length;
  const bodyOffset = sep ? sep.offset + sep.length : bytes.length;
  const headerText = new TextDecoder().decode(bytes.subarray(0, headerEnd));
  const body = sep ? bytes.subarray(bodyOffset) : null;

  const lines = headerText.split(/\r?\n/);
  const firstLine = lines[0] ?? "";
  const match = firstLine.match(/^(\S+)\s+(\S+)(?:\s+(.*))?$/);
  const isResponse = /^HTTP\//.test(firstLine);

  const headers: Array<{ key: string; value: string }> = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]!;
    const colon = line.indexOf(":");
    if (colon !== -1) {
      headers.push({
        key: line.slice(0, colon),
        value: line.slice(colon + 1).trim(),
      });
    }
  }

  return {
    isResponse,
    method: isResponse ? "" : (match?.[1] ?? ""),
    path: isResponse ? "" : (match?.[2] ?? ""),
    statusCode: isResponse ? (match?.[2] ?? "") : "",
    statusText: isResponse ? (match?.[3] ?? "") : "",
    httpVersion: isResponse ? (match?.[1] ?? "") : (match?.[3] ?? ""),
    headers,
    bodyBytes: body && body.length > 0 ? body : null,
  };
}

const Container = styled("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  padding: 12px;
  overflow-y: auto;
`;

const RequestLineRow = styled("div")`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding-bottom: 12px;
`;

const Badge = styled("span")`
  flex-shrink: 0;
  padding: 1px 5px;
  border-radius: 6px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  line-height: 20px;
`;

function badgeColors(code: string): { background: string; color: string } {
  const n = Number.parseInt(code, 10);
  if (n >= 200 && n < 300)
    return { background: "var(--green-400)", color: "var(--green-1000)" };
  if (n >= 300 && n < 400)
    return { background: "var(--sky-400)", color: "var(--sky-1000)" };
  if (n >= 400 && n < 500)
    return { background: "var(--orange-400)", color: "var(--orange-1000)" };
  if (n >= 500)
    return { background: "var(--red-400)", color: "var(--red-1000)" };
  return { background: "var(--alpha4)", color: "var(--text-primary)" };
}

const HeaderPanel = styled("div")`
  margin: 0 -12px;
  padding: 0 12px 0;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const HeadersSection = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 12px;
`;

const HeaderRow = styled("div")`
  display: flex;
  gap: 4px;
`;

const HeaderKey = styled("span")`
  white-space: nowrap;
  &::after {
    content: ":";
  }
`;

const HeaderValue = styled("span")`
  color: var(--text-secondary);
  word-break: break-all;
`;

const BodySection = styled("div")`
  padding-top: 12px;
  flex: 1;
  min-height: 0;
`;

export function Http({
  url,
  bleed,
  selectedRanges,
  onSelect,
  onControls,
}: Props) {
  const [rawData, setRawData] = useState<ArrayBuffer>();
  const [bodyControls, setBodyControls] = useState<FileRenderControl[]>([]);
  const [headersVisible, setHeadersVisible] = useState(
    () => localStorage.getItem("httpHeadersVisible") !== "false"
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetch(url, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (res) => {
        const size = Number(res.headers.get("Content-Length")) || 0;

        if (!res.body) return;

        if (size <= MAX_PREVIEW_BYTES) {
          setRawData(await res.arrayBuffer());
          return;
        }

        const reader = res.body.getReader();
        const chunks: Uint8Array[] = [];
        let bytesRead = 0;
        while (bytesRead < MAX_PREVIEW_BYTES) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          bytesRead += value.byteLength;
        }
        reader.cancel();

        const result = new Uint8Array(bytesRead);
        let offset = 0;
        for (const chunk of chunks) {
          result.set(chunk, offset);
          offset += chunk.byteLength;
        }
        setRawData(result.buffer);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [url]);

  const parsed = useMemo(
    () => (rawData ? parseHttpMessage(rawData) : null),
    [rawData]
  );

  useEffect(() => {
    if (!onControls || !parsed) return;
    onControls([
      {
        id: "http-headers",
        icon: "info",
        label: headersVisible ? "Hide details" : "Show details",
        active: headersVisible,
        onToggle: () => {
          setHeadersVisible(!headersVisible);
          localStorage.setItem("httpHeadersVisible", String(!headersVisible));
        },
      },
      ...bodyControls,
    ]);
    return () => onControls([]);
  }, [onControls, parsed, headersVisible, bodyControls]);

  if (!parsed) return null;

  // The server serves the stripped body with its own resolved Content-Type, so
  // FileRender renders it the same way it renders any file.
  const bodyUrl = `${url}${url.includes("?") ? "&" : "?"}body=1`;

  return (
    <Container
      style={
        bleed
          ? { margin: -bleed, height: `calc(100% + ${bleed * 2}px)` }
          : undefined
      }
    >
      {headersVisible && (
        <HeaderPanel>
          <RequestLineRow>
            {parsed.isResponse ? (
              <>
                <Badge style={badgeColors(parsed.statusCode)}>
                  {parsed.statusCode}
                </Badge>
                <Text variant="code-regular" color="var(--text-primary)">
                  {parsed.statusText}
                </Text>
                <Text
                  variant="code-regular"
                  color="var(--text-secondary)"
                  style={{ marginLeft: "auto", whiteSpace: "nowrap" }}
                >
                  {parsed.httpVersion}
                </Text>
              </>
            ) : (
              <>
                <Badge
                  style={{
                    background: "var(--alpha4)",
                    color: "var(--text-primary)",
                  }}
                >
                  {parsed.method}
                </Badge>
                <Text variant="code-regular" color="var(--text-primary)">
                  {parsed.path}
                </Text>
                {parsed.httpVersion && (
                  <Text
                    variant="code-regular"
                    color="var(--text-secondary)"
                    style={{ marginLeft: "auto", whiteSpace: "nowrap" }}
                  >
                    {parsed.httpVersion}
                  </Text>
                )}
              </>
            )}
          </RequestLineRow>

          {parsed.headers.length > 0 && (
            <HeadersSection>
              <Text variant="code-regular" color="var(--text-primary)">
                {parsed.headers.map((header, i) => (
                  <HeaderRow key={i}>
                    <HeaderKey>{header.key}</HeaderKey>
                    <HeaderValue>{header.value}</HeaderValue>
                  </HeaderRow>
                ))}
              </Text>
            </HeadersSection>
          )}
        </HeaderPanel>
      )}

      {parsed.bodyBytes && (
        <BodySection style={headersVisible ? undefined : { paddingTop: 0 }}>
          <FileRender
            url={bodyUrl}
            bleed={12}
            onControls={setBodyControls}
            onSelect={onSelect}
            selectedRanges={selectedRanges}
          />
        </BodySection>
      )}
    </Container>
  );
}
